import { createSlice } from '@reduxjs/toolkit';
import { ICreateOrderSliceInitialState } from 'src/app/Orders/order.interface';
import { RootState } from 'src/redux/store';

export type OrderKeysType = 'shipper_details' | 'consignee_details' | 'load_details' | 'order_details' | 'documents' | 'review_finalize';

export const ORDER_KEYS: Record<string, OrderKeysType> = {
    SHIPPER_DETAILS: 'shipper_details',
    CONSIGNEE_DETAILS: 'consignee_details',
    LOAD_DETAILS: 'load_details',
    ORDER_DETAILS: 'order_details',
    DOCUMENTS: 'documents',
    REVIEW_FINALIZE: 'review_finalize',
};

const initialState: ICreateOrderSliceInitialState = {
    loading: true,
    error: null,
    data: {
        orderInfo: null,
        isDraft: true,
        currentStep: ORDER_KEYS.SHIPPER_DETAILS,
        orderIsFromQuote: null,
        completedSteps: [],
        draftOrderLegId: null,
        isDraftSaving: false,
    },
    customerDropdownOptions: {
        recentCustomerList: [],
        searchCustomerList: [],
        divisionList: [],
        billingAccountList: [],
    },
    brokerList: [],
    shipperContacts: {
        recentShipperContacts: [],
        searchShipperContacts: [],
    },
    consigneeContacts: {
        recentConsigneeContacts: [],
        searchConsigneeContacts: [],
    },
};

export const CreateOrdersSlice = createSlice({
    name: 'CreateOrders',
    initialState,
    reducers: {
        setData: (state, { payload }) => {
            state.data = { ...state.data, ...payload };
        },
        setCreateOrderData: (state, { payload }) => {
            if (payload.data?.brokerDetails && state.data.orderInfo?.brokerDetails?.documents) {
                payload.data.brokerDetails = {
                    ...payload.data.brokerDetails,
                    documents: state.data.orderInfo.brokerDetails.documents,
                };
            }
            state.data.orderInfo = { ...state.data.orderInfo, ...payload.data };
        },
        setCreateOrderStep: (state, { payload }) => {
            state.data.currentStep = payload;
        },
        setCreateOrderIsDraft: (state, { payload }) => {
            state.data.isDraft = payload;
        },
        resetCreateOrder: (state) => {
            state.data.orderInfo = null;
            state.data.currentStep = ORDER_KEYS.SHIPPER_DETAILS;
            state.data.isDraft = true;
            state.data.completedSteps = [];
            state.data.draftOrderLegId = null;
            state.customerDropdownOptions = {
                recentCustomerList: [],
                searchCustomerList: [],
                divisionList: [],
                billingAccountList: [],
            };
            state.brokerList = [];
            state.shipperContacts = {
                recentShipperContacts: [],
                searchShipperContacts: [],
            };
            state.consigneeContacts = {
                recentConsigneeContacts: [],
                searchConsigneeContacts: [],
            };
            state.data.orderIsFromQuote = null;
            state.data.draftOrderLegId = null;
        },
        setCompletedStep: (state, { payload }) => {
            state.data.completedSteps = payload;
        },
        setIsDraftSaving: (state, { payload }) => {
            state.data.isDraftSaving = payload;
        },
        setRecentCustomerList: (state, { payload }) => {
            if (!state.customerDropdownOptions) {
                state.customerDropdownOptions = {
                    recentCustomerList: [],
                    searchCustomerList: [],
                    divisionList: [],
                    billingAccountList: [],
                };
            }
            state.customerDropdownOptions.recentCustomerList = payload;
        },
        setSearchCustomerList: (state, { payload }) => {
            if (!state.customerDropdownOptions) {
                state.customerDropdownOptions = {
                    recentCustomerList: [],
                    searchCustomerList: [],
                    divisionList: [],
                    billingAccountList: [],
                };
            }
            state.customerDropdownOptions.searchCustomerList = payload;
        },
        setDivisionList: (state, { payload }) => {
            if (!state.customerDropdownOptions) {
                state.customerDropdownOptions = {
                    recentCustomerList: [],
                    searchCustomerList: [],
                    divisionList: [],
                    billingAccountList: [],
                };
            }
            state.customerDropdownOptions.divisionList = payload;
        },
        setBillingAccountList: (state, { payload }) => {
            if (!state.customerDropdownOptions) {
                state.customerDropdownOptions = {
                    recentCustomerList: [],
                    searchCustomerList: [],
                    divisionList: [],
                    billingAccountList: [],
                };
            }
            state.customerDropdownOptions.billingAccountList = payload;
        },
        setShipperContacts: (state, { payload }) => {
            state.shipperContacts = { ...state.shipperContacts, ...payload };
        },
        setConsigneeContacts: (state, { payload }) => {
            state.consigneeContacts = { ...state.consigneeContacts, ...payload };
        },
        setBrokerList: (state, { payload }) => {
            state.brokerList = payload;
        },
        setDocuments: (state, { payload }) => {
            if (!state.data.orderInfo) {
                state.data.orderInfo = {
                    brokerDetails: { documents: [] },
                } as any;

                return;
            }

            if (!state.data.orderInfo.brokerDetails) {
                state.data.orderInfo.brokerDetails = { documents: [] };
            }
            state.data.orderInfo.brokerDetails.documents = payload;
        },
        addDocument: (state, { payload }) => {
            if (!state.data.orderInfo) {
                state.data.orderInfo = {
                    brokerDetails: { documents: [] },
                } as any;
            }
            const orderInfo = state.data.orderInfo;

            if (!orderInfo) {
                return;
            }

            if (!orderInfo.brokerDetails) {
                orderInfo.brokerDetails = { documents: [] };
            }

            if (!orderInfo.brokerDetails.documents) {
                orderInfo.brokerDetails.documents = [];
            }
            const existingIndex = orderInfo.brokerDetails.documents.findIndex((doc: any) => doc.id === payload.id);

            if (existingIndex !== -1) {
                orderInfo.brokerDetails.documents[existingIndex] = payload;
            } else {
                orderInfo.brokerDetails.documents.push(payload);
            }
        },
        removeDocument: (state, { payload }) => {
            const orderInfo = state.data.orderInfo;

            if (orderInfo?.brokerDetails?.documents && Array.isArray(orderInfo.brokerDetails.documents)) {
                orderInfo.brokerDetails.documents = orderInfo.brokerDetails.documents.filter((doc: any) => doc.id !== payload.documentId && doc.id !== payload.id);
            }
        },
    },
});

export const {
    setCreateOrderData,
    setCreateOrderStep,
    resetCreateOrder,
    setCreateOrderIsDraft,
    setData,
    setCompletedStep,
    setIsDraftSaving,
    setRecentCustomerList,
    setSearchCustomerList,
    setDivisionList,
    setBillingAccountList,
    setBrokerList,
    setDocuments,
    addDocument,
    removeDocument,
} = CreateOrdersSlice.actions;

export const createOrderInfo = (state: RootState): any => state.CreateOrders.data?.orderInfo;
export const shipperOrderInfo = (state: RootState): any => state.CreateOrders.data?.orderInfo?.shipperDetails;
export const consigneeOrderInfo = (state: RootState): any => state.CreateOrders.data?.orderInfo?.consigneeDetails;
export const carrierOrderInfo = (state: RootState): any => state.CreateOrders.data?.orderInfo?.carrierDetails;
export const createOrderIsDraft = (state: RootState): boolean => state.CreateOrders.data?.isDraft;
export const orderStepDetails = (state: RootState): any => state.CreateOrders.data?.orderInfo?.orderStepDetails;
export const orderDimensions = (state: RootState): any => state.CreateOrders.data?.orderInfo?.dimensionsDetails;
export const brokerDetails = (state: RootState): any => state.CreateOrders.data?.orderInfo?.brokerDetails;

export const customerPricingOrderInfo = (state: RootState): any => state.CreateOrders.data?.orderInfo?.customerPricingDetails;
export const createOrderCurrentStep = (state: RootState): any => state.CreateOrders.data?.currentStep;
export const createOrderLoading = (state: RootState): boolean => state.CreateOrders.loading;

export const createOrderError = (state: RootState): any => state.CreateOrders.error;

export const orderIsFromQuote = (state: RootState): any => state.CreateOrders.data?.orderIsFromQuote;

export const completedSteps = (state: RootState): string[] => state.CreateOrders.data?.completedSteps || [];

export const isDraftSaving = (state: RootState): boolean => state.CreateOrders.data?.isDraftSaving || false;

export const recentCustomerListOptions = (state: RootState): any[] => state.CreateOrders.customerDropdownOptions?.recentCustomerList || [];

export const searchCustomerListOptions = (state: RootState): any[] => state.CreateOrders.customerDropdownOptions?.searchCustomerList || [];

export const divisionListOptions = (state: RootState): any[] => state.CreateOrders.customerDropdownOptions?.divisionList || [];

export const billingAccountListOptions = (state: RootState): any[] => state.CreateOrders.customerDropdownOptions?.billingAccountList || [];

export const brokerListOptions = (state: RootState): any[] => state.CreateOrders.brokerList || [];

export const draftOrderLegId = (state: RootState): number | null => state.CreateOrders.data?.draftOrderLegId || null;

export const customsBrokerDocuments = (state: RootState): any[] => state.CreateOrders.data?.orderInfo?.brokerDetails?.documents || [];

export const selectedCustomerFromList = (state: RootState): any => {
    const customerPricingDetails = state.CreateOrders.data?.orderInfo?.customerPricingDetails;
    const orderCustomerMasterId = (customerPricingDetails as any)?.orderCustomerMasterId;
    if (!orderCustomerMasterId) return null;

    const recentList = state.CreateOrders.customerDropdownOptions?.recentCustomerList || [];
    const searchList = state.CreateOrders.customerDropdownOptions?.searchCustomerList || [];

    const allCustomers = [...recentList, ...searchList];

    return allCustomers.find((customer) => customer.value === orderCustomerMasterId) || null;
};

export default CreateOrdersSlice.reducer;
